import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Upload, Check, AlertTriangle, Eye, Download,
  ChevronRight, ChevronLeft, CheckCircle2, XCircle, Loader2,
  Shield, FileText, Smartphone, Copy, ExternalLink, RefreshCw,
  Monitor
} from 'lucide-react';
import { detectDevice } from '../lib/deviceDetection';
import { useLanguage } from '../context/LanguageContext';
import { QRCodeSVG } from 'qrcode.react';
import { compareFaces } from '../lib/biometrics';
import { extractTextFromImage, verifyDocumentAuthenticity } from '../lib/documentAnalysis';
import { validateFile, ALLOWED_FILE_TYPES } from '../lib/fileUtils';
import {
  PHILIPPINE_IDS, PhilippineIdType, getPhilippineIdsByPriority,
  extractPhilippineIdData, parsePhilSysQR, crossCheckQRData,
} from '../lib/philippineIds';
import {
  paymentService, VerificationType, PRICING_TIERS,
} from '../lib/payments';
import { creditService } from '../lib/credits';
import { verifications, auth } from '../lib/api';
import LivenessCheck from './LivenessCheck';
import { scanQRCode } from '../lib/qrScanner';
import DemoConsentModal from './DemoConsentModal';
import { isDemoMode, logDemoEvent } from '../lib/demoMode';
import { analyzeImageQuality, QualityResult } from '../lib/qualityCheck';
import { shouldRecommendMobileHandoff, getDeviceDescription } from '../lib/deviceDetection';
import { compressImage } from '../lib/imageCompression';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────
type WizardStep =
  | 'choose-bundle'
  | 'choose-type'
  | 'select-id'
  | 'upload-document'
  | 'review-ocr'
  | 'biometric-consent'
  | 'liveness-check'
  | 'processing'
  | 'result';

interface StepConfig {
  key: WizardStep;
  label: string;
  icon: React.ReactNode;
}

// STEPS is rebuilt as a translated array inside the component body (see below).

export default function IdentityVerification({ isAssisted = false }: { isAssisted?: boolean }) {
  const { t } = useLanguage();

  // ── State ────────────────────────────────────
  const [currentStep, setCurrentStep] = useState<WizardStep>('choose-bundle');
  const [verificationType, setVerificationType] = useState<VerificationType>('basic');
  const [selectedIdType, setSelectedIdType] = useState<PhilippineIdType | ''>('');
  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [idDocumentBack, setIdDocumentBack] = useState<File | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  const [ocrData, setOcrData] = useState<Record<string, string>>({});
  const [pendingOcrOverrides, setPendingOcrOverrides] = useState<Record<string, string>>({});
  const pendingOcrOverridesRef = useRef<Record<string, string>>({});
  const [rawOcrText, setRawOcrText] = useState<string>(''); // raw OCR output used for name cleanup validation
  const [ocrLoading, setOcrLoading] = useState(false);
  const [documentAuthentic, setDocumentAuthentic] = useState(false);
  const [selfieImage, setSelfieImage] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [livenessResult, setLivenessResult] = useState<{ passed: boolean; score: number } | null>(null);
  const [selectedBundleId, setSelectedBundleId] = useState<string>('starter'); // Default to popular bundle
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [blockchainTxHash, setBlockchainTxHash] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'verified' | 'failed'>('pending');
  const [error, setError] = useState<string | null>(null);
  const [useHandoff, setUseHandoff] = useState(false);
  const [selfieBlob, setSelfieBlob] = useState<Blob | null>(null);
  const [serverResult, setServerResult] = useState<{
    confidenceScore: number;
    ocrResult: any;
    faceResult: any;
    authenticityScore: number;
    failureReasons?: string[];
  } | null>(null);
  const [processingStage, setProcessingStage] = useState<string>('uploading');
  const [biometricConsent, setBiometricConsent] = useState(false);
  const [qrData, setQrData] = useState<Record<string, string>>({});
  const [qrMatchResult, setQrMatchResult] = useState<{ isMatch: boolean; mismatchedFields: string[] } | null>(null);
  const [qualityResult, setQualityResult] = useState<QualityResult | null>(null);
  const [isAnalyzingQuality, setIsAnalyzingQuality] = useState(false);
  const [showDesktopHandoffPrompt, setShowDesktopHandoffPrompt] = useState(false);
  const [desktopPromptDismissed, setDesktopPromptDismissed] = useState(false);
  const [activeBundles, setActiveBundles] = useState<any[]>([]);

  // Read CSRF token from cookie for authenticated fetch calls
  const getCsrf = () => {
    const m = document.cookie.match(/(?:^|;\s*)agos_csrf=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : '';
  };

  // Helper to save OCR overrides before proceeding
  const handleSaveOCR = async () => {
    // Buffer the current OCR edits so they can be flushed when verificationId becomes available
    // (verificationId is only created after payment, which happens after liveness)
    const currentData = { ...ocrData };
    setPendingOcrOverrides(currentData);
    pendingOcrOverridesRef.current = currentData;

    // If verificationId already exists (e.g. document handoff completed), save immediately too
    if (verificationId && Object.keys(currentData).length > 0) {
      try {
        setOcrLoading(true);
        await fetch(`/api/verifications/${verificationId}/ocr-overrides`, {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrf()
          },
          body: JSON.stringify({ ocrData: currentData }),
        });
      } catch (err) {
        console.error('Failed to save OCR overrides immediately', err);
        // Non-blocking: pending overrides will still be flushed during processing
      } finally {
        setOcrLoading(false);
      }
    }

    goTo('biometric-consent');
  };

  // Camera capture for ID document
  type CaptureMode = 'choose' | 'camera' | 'file';
  const [captureMode, setCaptureMode] = useState<CaptureMode>('choose');
  const [docCameraActive, setDocCameraActive] = useState(false);
  const [qualityBlocked, setQualityBlocked] = useState(false);
  const docVideoRef = useRef<HTMLVideoElement>(null);
  const docCanvasRef = useRef<HTMLCanvasElement>(null);
  const docStreamRef = useRef<MediaStream | null>(null);
  const [captureSide, setCaptureSide] = useState<'front' | 'back'>('front');


  // Demo mode state
  const [showDemoConsent, setShowDemoConsent] = useState(isDemoMode());
  const [demoConsentGiven, setDemoConsentGiven] = useState(false);

  // Mobile handoff state
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  const [handoffStatus, setHandoffStatus] = useState<'idle' | 'creating' | 'waiting' | 'mobile_opened' | 'completed' | 'error' | 'expired'>('idle');
  const [handoffType, setHandoffType] = useState<'liveness' | 'document'>('liveness');
  const handoffPollRef = useRef<NodeJS.Timeout | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Build translated steps inside the component
  const STEPS: StepConfig[] = [
    { key: 'choose-bundle',     label: t('step_bundle'),     icon: <img src="/pwa-192x192.png" alt="Agos" className="w-4 h-4 object-contain" /> },
    { key: 'choose-type',       label: t('step_type'),       icon: <FileText className="w-4 h-4" /> },
    { key: 'select-id',         label: t('step_id'),         icon: <FileText className="w-4 h-4" /> },
    { key: 'upload-document',   label: t('step_upload'),     icon: <Upload className="w-4 h-4" /> },
    { key: 'review-ocr',        label: t('step_review'),     icon: <Eye className="w-4 h-4" /> },
    { key: 'biometric-consent', label: t('step_consent'),    icon: <Shield className="w-4 h-4" /> },
    { key: 'liveness-check',    label: t('step_liveness'),   icon: <Camera className="w-4 h-4" /> },
    { key: 'processing',        label: t('step_processing'), icon: <Loader2 className="w-4 h-4" /> },
    { key: 'result',            label: t('step_result'),     icon: <CheckCircle2 className="w-4 h-4" /> },
  ];

  // ── Helpers ──────────────────────────────────
  const stepIndex = STEPS.findIndex(s => s.key === currentStep);

  const goTo = useCallback((step: WizardStep) => {
    setError(null);
    setCurrentStep(step);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  // Clean up camera on unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  // Clean up handoff polling on unmount
  useEffect(() => {
    return () => {
      if (handoffPollRef.current) {
        clearInterval(handoffPollRef.current);
      }
    };
  }, []);

  // Fetch user credits and active bundles on mount
  useEffect(() => {
    const fetchData = async () => {
      if (auth.isAuthenticated()) {
        try {
          const [balance, bundles] = await Promise.all([
            creditService.getBalance(),
            creditService.getBundles(),
          ]);
            setActiveBundles(bundles);
          
          // Auto-select the first available bundle if 'starter' isn't found
          if (bundles.length > 0) {
            const starter = bundles.find((b: any) => b.name.toLowerCase().includes('starter'));
            if (starter) {
              setSelectedBundleId(starter.id);
            } else {
              setSelectedBundleId(bundles[0].id);
            }
          }
        } catch (err) {
          console.error('Failed to fetch credit data:', err);
        }
      }
    };
    fetchData();
  }, []);

  // ── Recovery from PayMongo redirect ─────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get('payment') === 'success';
    const verIdFromUrl = params.get('verificationId');

    if (isSuccess && verIdFromUrl) {
      console.log('Recovering verification state for ID:', verIdFromUrl);
      setVerificationId(verIdFromUrl);
      
      const fetchAndResume = async () => {
        try {
          // Fetch verification details to restore UI state (type, ID category)
          const v = await verifications.get(verIdFromUrl);
          if (v) {
            if (v.verificationType) setVerificationType(v.verificationType as VerificationType);
            if (v.idType) setSelectedIdType(v.idType as PhilippineIdType);
          }
          
          // Transition directly to processing
          runServerProcessing(verIdFromUrl);

          // Clean up URL once parameters are consumed by the wizard
          window.history.replaceState({}, '', window.location.pathname);
        } catch (err) {
          console.error('Failed to fully recover verification data, attempting processing anyway:', err);
          runServerProcessing(verIdFromUrl);
          window.history.replaceState({}, '', window.location.pathname);
        }
      };

      fetchAndResume();
    }
  }, []);

  // Desktop detection - show handoff prompt when reaching liveness check
  useEffect(() => {
    if (currentStep === 'liveness-check' && !desktopPromptDismissed && !isAssisted) {
      const shouldPrompt = shouldRecommendMobileHandoff();
      setShowDesktopHandoffPrompt(shouldPrompt);
      // NOTE: We intentionally do NOT auto-switch to handoff.
      // The user can see the recommendation banner and choose themselves via the toggle.
    }
  }, [currentStep, desktopPromptDismissed, isAssisted]);

  // Create mobile handoff session
  const createHandoffSession = async (type: 'liveness' | 'document' = 'liveness') => {
    setHandoffStatus('creating');
    setError(null);
    setHandoffType(type);
    setUseHandoff(true); // Enable handoff UI mode

    try {
      // First create a verification record if we don't have one
      let currentVerificationId = verificationId;

      if (!currentVerificationId) {
        const isLoggedIn = auth.isAuthenticated();
        const idNumber = ocrData.psn || ocrData.licenseNumber || ocrData.sssNumber ||
          ocrData.tin || ocrData.pin || ocrData.crn || ocrData.passportNumber || 'PENDING_HANDOFF';

        if (isLoggedIn) {
          const verification = await verifications.create({
            idType: selectedIdType as string,
            idNumber,
            isAssisted,
            metadata: {
              verificationType,
              ocrData: type === 'document' ? {} : ocrData, // Empty OCR for doc handoff
              documentAuthentic,
            },
          });
          currentVerificationId = verification.id;
          setVerificationId(verification.id);

          // Upload documents to the verification (ONLY if we have them locally and it's NOT document handoff)
          // If it IS document handoff, we don't have documents yet
          if (idDocument && type !== 'document') {
            await verifications.uploadDocument(
              verification.id,
              idDocument,
              idDocumentBack || undefined
            );
          }
        } else {
          setError('Please log in to use mobile handoff');
          setHandoffStatus('error');
          return;
        }
      }

      const session = await verifications.createHandoffSession(currentVerificationId!, type);
      const url = `${window.location.origin}/handoff/${session.sessionId}?secret=${session.secret}`;
      setHandoffUrl(url);
      setHandoffStatus('waiting');

      // Start polling for completion
      startHandoffPolling(session.sessionId, type);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create handoff session');
      setHandoffStatus('error');
    }
  };

  // Poll handoff status
  const startHandoffPolling = (sessionId: string, sessionType: 'liveness' | 'document') => {
    // Clear any existing polling
    if (handoffPollRef.current) {
      clearInterval(handoffPollRef.current);
    }

    const pollInterval = setInterval(async () => {
      try {
        const status = await verifications.getHandoffStatus(sessionId);

        // Update status for UI feedback
        if (status.status === 'mobile_opened' && handoffStatus !== 'mobile_opened') {
          setHandoffStatus('mobile_opened');
        }

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setHandoffStatus('completed');
          
          // Use server status if available, fallback to local type
          // CRITICAL: We prioritize the local sessionType to avoid incorrect redirections
          const effectiveType = sessionType; 
          
          if (effectiveType === 'document') {
             // Document handoff completed
             // Fetch the verification to get the server-side OCR results
             try {
               const verifyDetails = await verifications.get(status.verificationId);
               
                if (verifyDetails.ocrData) {
                  setOcrData(verifyDetails.ocrData);
                }

                if (verifyDetails.qrData) {
                  setQrData(verifyDetails.qrData);
                }

                if (verifyDetails.qrMatchStatus) {
                  if (verifyDetails.qrMatchStatus === 'match') {
                    setQrMatchResult({ isMatch: true, mismatchedFields: [] });
                  } else if (verifyDetails.qrMatchStatus === 'mismatch') {
                    // Ideally server returns mismatched fields, but for now we default
                    setQrMatchResult({ isMatch: false, mismatchedFields: ['unknown'] }); 
                  } else if (verifyDetails.qrMatchStatus === 'no_qr_found') {
                     setQrMatchResult(null);
                  }
                }

                // If images are uploaded, mark as such (though we don't have the File objects)
                // We rely on documentPreview if possible, or just skip preview if not available
                // verifyDetails.document_front_path is on server. We can't easily show preview here 
                // unless we fetch it as blob, but that might be slow.
                // For now, let's assume the user trusts the capture.
                
                // Reset handoff status so the Liveness step starts fresh (showing "Generate QR")
                setHandoffStatus('idle');
                goTo('review-ocr');
             } catch (e) {
               console.error('Failed to fetch verification after handoff', e);
             }
          } else {
             // Liveness handoff completed
             setLivenessResult({ passed: true, score: status.livenessScore || 0.9 });
             // Auto-advance
             handleBundlePayment();
          }
        } else if (status.status === 'expired') {
          clearInterval(pollInterval);
          setHandoffStatus('expired');
          setError('Handoff session expired. Please try again.');
        }
      } catch (err) {
        console.warn('Handoff poll error:', err);
      }
    }, 2000);
    
    handoffPollRef.current = pollInterval;
  };

  // Copy handoff URL to clipboard
  const copyHandoffUrl = async () => {
    if (handoffUrl) {
      try {
        await navigator.clipboard.writeText(handoffUrl);
        // Could add a toast notification here
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Handle payment callback from PayMongo redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentId = urlParams.get('paymentId');
    const purchaseId = urlParams.get('purchaseId');
    const verificationId = urlParams.get('verificationId');

    if (paymentStatus && paymentId) {
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);

      if (paymentStatus === 'success') {
        // Payment successful - poll for confirmation
        setVerificationId(verificationId);
        goTo('processing');
        if (purchaseId) {
          pollPurchaseStatus(purchaseId, verificationId || undefined);
        } else if (paymentId) {
          pollPaymentStatus(paymentId, verificationId || undefined);
        }
      } else if (paymentStatus === 'failed') {
        // Payment failed
        setError('Payment was cancelled or failed. Please try again.');
        goTo('choose-bundle'); // Go back to bundle selection
      }
    }
  }, [goTo]);

  // Poll purchase status until credits are added
  const pollPurchaseStatus = async (purchaseId: string, verificationId: string | undefined) => {
    let attempts = 0;
    const maxAttempts = 30;

    const poll = async () => {
      try {
        const status = await creditService.getPurchaseStatus(purchaseId);

        if (status.purchaseStatus === 'completed') {
          runServerProcessing(verificationId);
        } else if (status.purchaseStatus === 'failed') {
          setError('Payment failed. Please try again.');
          goTo('choose-bundle');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000);
        } else {
          // Timeout - try proceeding anyway, maybe webhook handled it
          runServerProcessing(verificationId);
        }
      } catch (err) {
        // Error checking - proceed to check if verification works
        runServerProcessing(verificationId);
      }
    };

    poll();
  };

  // Poll payment status until completed
  const pollPaymentStatus = async (paymentId: string, verificationId: string | undefined) => {
    let attempts = 0;
    const maxAttempts = 30; // Poll for up to 30 seconds

    const poll = async () => {
      try {
        const status = await paymentService.getPaymentStatus(paymentId);

        if (status.status === 'completed') {
          // Payment confirmed - proceed with verification
          runServerProcessing(verificationId);
        } else if (status.status === 'failed') {
          setError('Payment failed. Please try again.');
          goTo('choose-bundle'); // Go back to bundle selection
        } else if (attempts < maxAttempts) {
          // Still processing - poll again in 1 second
          attempts++;
          setTimeout(poll, 1000);
        } else {
          // Timeout - assume success and proceed
          runServerProcessing(verificationId);
        }
      } catch (err) {
        // Error checking status - assume success and proceed
        runServerProcessing(verificationId);
      }
    };

    poll();
  };

  // IDs that need a back photo
  const needsBack = (idType: PhilippineIdType) =>
    ['drivers_license', 'umid', 'postal_id', 'prc'].includes(idType);

  // ── Step Handlers ────────────────────────────

  // ── Document camera helpers ──────────────────
  const startDocCamera = useCallback(async (side: 'front' | 'back') => {
    try {
      setError(null);
      setCaptureSide(side);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } },
      });
      docStreamRef.current = stream;
      setDocCameraActive(true);
      // Attach stream to video element after render
      setTimeout(() => {
        if (docVideoRef.current && docStreamRef.current) {
          docVideoRef.current.srcObject = docStreamRef.current;
          docVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (err) {
      setError('Could not access camera. Please upload a file instead.');
      setCaptureMode('file');
    }
  }, []);

  const stopDocCamera = useCallback(() => {
    if (docStreamRef.current) {
      docStreamRef.current.getTracks().forEach(t => t.stop());
      docStreamRef.current = null;
    }
    setDocCameraActive(false);
  }, []);

  const captureDocPhoto = useCallback(async () => {
    if (!docVideoRef.current || !docCanvasRef.current) return;
    const ctx = docCanvasRef.current.getContext('2d');
    if (!ctx) return;

    docCanvasRef.current.width = docVideoRef.current.videoWidth;
    docCanvasRef.current.height = docVideoRef.current.videoHeight;
    ctx.drawImage(docVideoRef.current, 0, 0);

    const blob = await new Promise<Blob>((resolve, reject) =>
      docCanvasRef.current!.toBlob(b => b ? resolve(b) : reject(new Error('Capture failed')), 'image/jpeg', 0.92)
    );

    // Compress image client-side to avoid 413
    setProcessingStage('Optimizing image...');
    const compressedBlob = await compressImage(blob);

    const file = new File([compressedBlob], `id-${captureSide}-${Date.now()}.jpg`, { type: 'image/jpeg' });
    stopDocCamera();
    await handleDocumentUpload(file, captureSide);
  }, [captureSide, stopDocCamera]);

  // Clean up doc camera on unmount
  useEffect(() => () => stopDocCamera(), [stopDocCamera]);

  // Step 3: Document upload + OCR (with enforced quality checks)
  const handleDocumentUpload = async (file: File, side: 'front' | 'back') => {
    try {
      setError(null);
      setQualityBlocked(false);
      
      // Compress image client-side to avoid 413
      setProcessingStage('Optimizing image...');
      const compressedBlob = await compressImage(file);
      const compressedFile = new File([compressedBlob], file.name, { type: 'image/jpeg' });

      await validateFile(compressedFile);

      if (side === 'front') {
        setIdDocument(compressedFile);
        setDocumentPreview(URL.createObjectURL(compressedFile));
        
        // Analyze image quality
        setIsAnalyzingQuality(true);
        try {
          const quality = await analyzeImageQuality(file);
          setQualityResult(quality);
          
          if (!quality.isAcceptable) {
            // BLOCK upload and show retake prompt
            setQualityBlocked(true);
            setError(null); // Don't show generic error — quality panel handles messaging
          }
        } catch (qualityErr) {
          console.warn('Quality analysis failed:', qualityErr);
          // Don't block upload if quality check itself fails
        } finally {
          setIsAnalyzingQuality(false);
        }
      } else {
        setIdDocumentBack(file);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid file');
    }
  };

  // Force override quality block ("Use Anyway")
  const overrideQualityBlock = () => {
    setQualityBlocked(false);
  };

  // Retake photo: clear current image and return to capture mode
  const retakePhoto = () => {
    setIdDocument(null);
    setDocumentPreview(null);
    setQualityResult(null);
    setQualityBlocked(false);
    setError(null);
    // Return to the mode they were using
    if (captureMode === 'camera') {
      startDocCamera('front');
    }
  };

  const runOCR = async () => {
    if (!idDocument || !selectedIdType) return;
    setOcrLoading(true);
    setError(null);

    try {
      if (verificationType === 'basic') {
        const { isAuthentic: isDocAuthentic } = await verifyDocumentAuthenticity(idDocument);
        setDocumentAuthentic(isDocAuthentic);
        await new Promise(resolve => setTimeout(resolve, 800));

        // Always run OCR in basic mode so user can clean up name before proceeding
        const rawText = await extractTextFromImage(idDocument);
        const extracted = extractPhilippineIdData(rawText, selectedIdType);
        // Ensure a name field always exists for user to edit (OCR may miss it for TIN, etc.)
        const nameFields = ['fullName', 'firstName', 'lastName', 'surname', 'givenNames'];
        if (!nameFields.some(f => f in extracted)) {
          extracted.fullName = '';
        }
        setOcrData(extracted);
        // Store raw OCR text so we can validate cleanup edits against it
        setRawOcrText(rawText);
        goTo('review-ocr');
      } else {
        setProcessingStage('Analyzing QR Security Signature...');

        // Scan QR from both front (unlikely) and back (highly likely)
        let qrText = await scanQRCode(idDocument);
        if (!qrText && idDocumentBack) {
          qrText = await scanQRCode(idDocumentBack);
        }

        const text = await extractTextFromImage(idDocument);
        const extracted = extractPhilippineIdData(text, selectedIdType);
        // Ensure a name field always exists for user to edit (OCR may miss it for TIN, etc.)
        const nameFields = ['fullName', 'firstName', 'lastName', 'surname', 'givenNames'];
        if (!nameFields.some(f => f in extracted)) {
          extracted.fullName = '';
        }
        setOcrData(extracted);
        setRawOcrText(text); // keep raw text for enhanced path too
        setPendingOcrOverrides({}); // Clear any pending overrides when new OCR data is set

        if (qrText && selectedIdType === 'philid') {
          const parsedQR = parsePhilSysQR(qrText);
          setQrData(parsedQR);
          const matchResult = crossCheckQRData(extracted, parsedQR);
          setQrMatchResult(matchResult);

          if (!matchResult.isMatch) {
            console.warn('QR Data Mismatch Detected:', matchResult.mismatchedFields);
            // We still proceed but with a warning or internal flag
          }
        }

        const { isAuthentic: isDocAuthentic } = await verifyDocumentAuthenticity(idDocument);
        setDocumentAuthentic(isDocAuthentic);

        goTo('review-ocr');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Processing failed. You can still proceed.');
      goTo('biometric-consent');
    } finally {
      setOcrLoading(false);
    }
  };

  // Step 5: Liveness check
  // Ensure video plays when camera becomes active
  useEffect(() => {
    if (cameraActive && streamRef.current && videoRef.current) {
      console.log('Attaching stream to video element');
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(err => console.warn('Video play failed:', err));
    }
  }, [cameraActive]);

  const startCamera = async () => {
    try {
      setError(null);
      console.log('Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      console.log('Camera access granted');
      streamRef.current = stream;
      setCameraActive(true);
    } catch (err) {
      console.error('Camera start failed:', err);
      setError('Failed to access camera. Please allow camera access or use mobile handoff.');
    }
  };

  const captureSelfie = async () => {
    // This is now predominantly handled by the LivenessCheck component.
    // However, we keep a minimal version for manual capture if needed,
    // or as a transition point.
    if (!videoRef.current || !canvasRef.current) return;

    try {
      const context = canvasRef.current.getContext('2d');
      if (!context) throw new Error('Could not get canvas context');

      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);

      const blob = await new Promise<Blob>((resolve, reject) =>
        canvasRef.current!.toBlob(b => b ? resolve(b) : reject(new Error('Capture failed')), 'image/jpeg', 0.9)
      );

      // Compress image client-side to avoid 413
      setProcessingStage('Optimizing image...');
      const compressedBlob = await compressImage(blob);

      setSelfieBlob(compressedBlob);
      setSelfieImage(URL.createObjectURL(compressedBlob));
      stopCamera();
      handleBundlePayment(); // Transition to payment/processing
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Selfie capture failed');
    }
  };

  // Step 6: Bundle Payment - Create checkout session and redirect to PayMongo
  const handleBundlePayment = async (manualSelfieBlob?: Blob | null) => {
    setPaymentProcessing(true);
    setError(null);

    try {
      const isLoggedIn = auth.isAuthenticated();
      const idNumber = ocrData.psn || ocrData.licenseNumber || ocrData.sssNumber ||
        ocrData.tin || ocrData.pin || ocrData.crn || ocrData.passportNumber || 'UNKNOWN';

      // Create verification record first if not already created (e.g., via handoff)
      let currentVerification;
      let verId = verificationId;
      
      if (isLoggedIn) {
        // CHECK CREDITS FIRST
        const currentBalance = await creditService.getBalance();
        const hasEnough = verificationType === 'enhanced' 
          ? currentBalance.enhancedCredits > 0 
          : currentBalance.basicCredits > 0;

        if (!verId) {
          currentVerification = await verifications.create({
            idType: selectedIdType as string,
            idNumber,
            isAssisted,
            ocrOverrides: pendingOcrOverridesRef.current,
            metadata: {
              verificationType,
              ocrData,
              documentAuthentic,
            },
          });
          verId = currentVerification.id;
          setVerificationId(verId);
        }

        // Flush any pending OCR overrides that were buffered during the review step
        // (they couldn't be saved earlier because verificationId didn't exist yet)
        // IMPORTANT: must await this so the override is saved before processing starts
        // Use ref to avoid stale closure — state may be outdated in this callback
        const overridesToFlush = pendingOcrOverridesRef.current;
        if (verId && Object.keys(overridesToFlush).length > 0) {
          try {
            await fetch(`/api/verifications/${verId}/ocr-overrides`, {
              method: 'PATCH',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getCsrf()
              },
              body: JSON.stringify({ ocrData: overridesToFlush }),
            });
          } catch (err) {
            console.warn('Failed to flush pending OCR overrides:', err);
          }
        }

        // UPLOAD DOCUMENTS AND SELFIE before any redirect.
        // This MUST complete before we send the user to PayMongo — on return the
        // page is reloaded and idDocument/selfieBlob state will be null.
        setProcessingStage('Saving photos...');
        try {
          await uploadFilesToServer(verId, manualSelfieBlob);
        } catch (uploadErr) {
          // Upload failed — do NOT redirect. Show the error so the user can retry.
          console.error('File upload failed before payment redirect', uploadErr);
          throw new Error('Failed to save your photos. Please check your connection and try again.');
        }

        // IF HAS CREDITS, SKIP PAYMENT
        if (hasEnough) {
          setProcessingStage('Using existing credits...');
          runServerProcessing(verId);
          return;
        }
      }

      // If already have a verification ID (e.g., from handoff) and payment is not needed, proceed to processing
      if (verificationId && !isLoggedIn) { 
        runServerProcessing(verificationId);
        return;
      }

      // If we reach here, we need to purchase credits
      // Ensure we have a valid bundle ID (prefer UUID from activeBundles)
      let bundleToPurchase = selectedBundleId;
      const realBundle = activeBundles.find(b => b.id === selectedBundleId || b.name.toLowerCase().includes('starter'));
      if (realBundle) {
        bundleToPurchase = realBundle.id;
      }

      // Purchase credit bundle
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?payment=success&verificationId=${verId || ''}`;
      const failureUrl = `${currentUrl}?payment=failed`;

      const response = await creditService.purchaseBundle({
        bundleId: bundleToPurchase,
        paymentMethod: 'qrph',
        successUrl,
        failureUrl,
      });

      // Redirect to PayMongo checkout page
      window.location.href = response.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
      setPaymentProcessing(false);
    }
  };

  // Upload files to server (shared helper)
  const uploadFilesToServer = async (verId: string, manualSelfieBlob?: Blob | null) => {
    // Upload documents
    if (idDocument) {
      setProcessingStage('uploading (ID)');
      await verifications.uploadDocument(verId, idDocument, idDocumentBack || undefined);
    }

    // Upload selfie (prioritize manual argument if provided, to fix race condition)
    const blobToUpload = manualSelfieBlob || selfieBlob;
    if (blobToUpload) {
      setProcessingStage('uploading (Selfie)');
      const selfieFile = new File([blobToUpload], 'selfie.jpg', { type: 'image/jpeg' });
      await verifications.uploadSelfie(verId, selfieFile);
    }
  };

  // Step 7: Server-side verification processing
  const runServerProcessing = async (verId?: string) => {
    if (!verId) {
      // Not logged in — no server record was created, just show a success result
      setVerificationStatus('verified');
      setTimeout(() => goTo('result'), 1500);
      return;
    }

    goTo('processing'); // Ensure we are on the processing step

    try {
      // Files were already uploaded in handleBundlePayment before any PayMongo
      // redirect. After a page reload idDocument/selfieBlob are null, so this
      // block only fires when processing is called without a prior redirect
      // (e.g. credits path). It is safe to skip when state is empty.
      setProcessingStage('uploading');
      if (idDocument || selfieBlob) {
        try {
          await uploadFilesToServer(verId);
        } catch (uploadErr) {
          console.warn('Redundant upload attempt failed (files may already be on server)', uploadErr);
        }
      }

      // Trigger server-side processing (OCR + face match + scoring)
      setProcessingStage('processing');
      const result = await verifications.process(verId);
      setServerResult({
        confidenceScore: result.confidenceScore,
        ocrResult: result.ocrResult,
        faceResult: result.faceResult,
        authenticityScore: result.authenticityScore,
        failureReasons: result.failureReasons,
      });

      // Check result
      if (result.status === 'completed' || result.status === 'verified') {
        setVerificationStatus('verified');
      } else {
        setVerificationStatus('failed');
      }

      goTo('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Server processing failed');
      setVerificationStatus('failed');
      goTo('result');
    }
  };

  // ── Render Helpers ───────────────────────────
  const idOptions = getPhilippineIdsByPriority();

  // ── Progress Bar ─────────────────────────────
  const ProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between relative">
        {/* Background line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-700" />
        {/* Active line */}
        <div
          className="absolute top-4 left-0 h-0.5 bg-blue-500 transition-all duration-500"
          style={{ width: `${(stepIndex / (STEPS.length - 1)) * 100}%` }}
        />

        {STEPS.map((step, idx) => {
          const isComplete = idx < stepIndex;
          const isCurrent = idx === stepIndex;
          return (
            <div key={step.key} className="relative flex flex-col items-center z-10">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${isComplete
                  ? 'bg-green-500 border-green-500 text-white'
                  : isCurrent
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-gray-800 border-gray-600 text-gray-500'
                  }`}
              >
                {isComplete ? <Check className="w-4 h-4" /> : step.icon}
              </div>
              <span className={`mt-1 text-xs hidden sm:block ${isCurrent ? 'text-blue-400 font-medium' : isComplete ? 'text-green-400' : 'text-gray-500'
                }`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Step Content ─────────────────────────────
  const renderStep = () => {
    switch (currentStep) {
      // ── Step 1: Choose bundle ──
      case 'choose-bundle':
        return (
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('bundle_title')}</h3>
            <p className="text-gray-400 mb-6">{t('bundle_sub')}</p>

            {/* Bundle Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {paymentService.getAvailableBundles().map(bundle => {
                const isSelected = selectedBundleId === bundle.id;
                const basicSavings = paymentService.calculateBundleSavingsPercent(bundle, 'basic');
                const enhancedSavings = paymentService.calculateBundleSavingsPercent(bundle, 'enhanced');

                return (
                  <button
                    key={bundle.id}
                    onClick={() => setSelectedBundleId(bundle.id)}
                    className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-900/20'
                        : bundle.popular
                          ? 'border-purple-500 bg-purple-900/20 hover:border-purple-400'
                          : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'
                    }`}
                  >
                    {/* Badge */}
                    {bundle.badge && (
                      <div className="absolute top-2 right-2 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">
                        {bundle.badge}
                      </div>
                    )}

                    {/* Bundle Name & Price */}
                    <div className="mb-3">
                      <h4 className="font-semibold text-white">{bundle.name}</h4>
                      <p className="text-2xl font-bold text-green-400">₱{bundle.price.toLocaleString()}</p>
                    </div>

                    {/* Credits */}
                    <div className="space-y-2 text-sm mb-3">
                      <div className="flex justify-between text-gray-300">
                        <span>Basic Credits:</span>
                        <span className="font-semibold text-blue-400">{bundle.basicCredits}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Enhanced Credits:</span>
                        <span className="font-semibold text-purple-400">{bundle.enhancedCredits}</span>
                      </div>
                    </div>

                    {/* Effective Prices */}
                    <div className="space-y-1 text-xs text-gray-400 border-t border-gray-700 pt-2">
                      <div className="flex justify-between">
                        <span>Basic: ~₱{paymentService.calculateBundleEffectivePrice(bundle, 'basic').toFixed(2)}/ea</span>
                        <span className="text-green-400">-{basicSavings.toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Enhanced: ~₱{paymentService.calculateBundleEffectivePrice(bundle, 'enhanced').toFixed(2)}/ea</span>
                        <span className="text-green-400">-{enhancedSavings.toFixed(0)}%</span>
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="mt-3 flex items-center text-blue-400 text-sm font-medium">
                        <Check className="w-4 h-4 mr-1" /> Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Info Box */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-6">
              <p className="text-blue-300 text-sm">💡 {t('bundle_info')}</p>
            </div>

            {/* Continue Button */}
            <div className="flex justify-end">
              <button
                onClick={() => goTo('choose-type')}
                className="flex items-center px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {t('bundle_continue')} <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        );

      // ── Step 2: Choose verification type ──
      case 'choose-type':
        return (
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('type_title')}</h3>
            <p className="text-gray-400 mb-6">{t('type_sub')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Basic */}
              <button
                onClick={() => { setVerificationType('basic'); goTo('select-id'); }}
                className={`p-6 rounded-lg border-2 text-left transition-all hover:border-blue-500 ${verificationType === 'basic' ? 'border-blue-500 bg-blue-900/20' : 'border-gray-700 bg-gray-800/50'
                  }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-semibold text-white">{t('type_basic')}</span>
                  <span className="text-2xl font-bold text-blue-400">
                    {paymentService.formatCurrency(PRICING_TIERS.standard.basicPhp)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mb-3">{t('type_basic_desc')}</p>
                <ul className="space-y-1.5 text-sm text-gray-300">
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-green-400 mr-2 flex-shrink-0" /> Face Match (Selfie vs ID)</li>
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-green-400 mr-2 flex-shrink-0" /> Liveness detection</li>
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-green-400 mr-2 flex-shrink-0" /> Instant verification results</li>
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-green-400 mr-2 flex-shrink-0" /> All Philippine IDs supported</li>
                </ul>
              </button>

              {/* Enhanced */}
              <button
                onClick={() => { setVerificationType('enhanced'); goTo('select-id'); }}
                className={`p-6 rounded-lg border-2 text-left transition-all hover:border-purple-500 relative ${verificationType === 'enhanced' ? 'border-purple-500 bg-purple-900/20' : 'border-gray-700 bg-gray-800/50'
                  }`}
              >
                <span className="absolute -top-2.5 right-4 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  {t('type_recommended')}
                </span>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-semibold text-white">{t('type_enhanced')}</span>
                  <span className="text-2xl font-bold text-purple-400">
                    {paymentService.formatCurrency(PRICING_TIERS.standard.enhancedPhp)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mb-3">{t('type_enhanced_desc')}</p>
                <ul className="space-y-1.5 text-sm text-gray-300">
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-green-400 mr-2 flex-shrink-0" /> Everything in Basic</li>
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-purple-400 mr-2 flex-shrink-0" /> AI OCR Text Extraction</li>
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-purple-400 mr-2 flex-shrink-0" /> Cryptographic Audit Trail</li>
                  <li className="flex items-center"><Check className="w-3.5 h-3.5 text-purple-400 mr-2 flex-shrink-0" /> PhilSys QR Format Validation</li>
                </ul>
              </button>
            </div>
          </div>
        );

      // ── Step 2: Select ID type ──
      case 'select-id':
        return (
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('id_title')}</h3>
            <p className="text-gray-400 mb-6">{t('id_sub')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
              {idOptions.map(id => (
                <button
                  key={id.type}
                  onClick={() => setSelectedIdType(id.type)}
                  className={`p-3 rounded-lg border text-left transition-all ${selectedIdType === id.type
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">{id.name}</p>
                      <p className="text-gray-500 text-xs">{id.agency}</p>
                    </div>
                    {id.priority === 'high' && (
                      <span className="text-xs bg-green-900/50 text-green-400 px-1.5 py-0.5 rounded">{t('id_primary')}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => goTo('choose-type')}
                className="flex items-center text-gray-400 hover:text-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </button>
              <button
                onClick={() => selectedIdType && goTo('upload-document')}
                disabled={!selectedIdType}
                className="flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        );

      // ── Step 3: Upload document ──
      case 'upload-document': {
        const device = detectDevice();
        const isDesktopDevice = device.isDesktop;

        return (
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('upload_title')}</h3>
            <p className="text-gray-400 mb-6">
              Capture or upload a clear photo of your {selectedIdType ? PHILIPPINE_IDS[selectedIdType].name : 'ID'}
            </p>

            {/* Capture Mode Chooser */}
            {captureMode === 'choose' && !documentPreview && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => {
                    if (isDesktopDevice) {
                      // On desktop, start document handoff
                      createHandoffSession('document');
                    } else {
                      setCaptureMode('camera');
                      startDocCamera('front');
                    }
                  }}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-600 hover:border-blue-500 hover:bg-blue-900/10 transition-all group"
                >
                  <div className="w-14 h-14 rounded-full bg-blue-600/20 flex items-center justify-center group-hover:bg-blue-600/30 transition-colors">
                    <Camera className="w-7 h-7 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('upload_take_photo')}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {isDesktopDevice ? 'Use your phone camera' : 'Use your camera'}
                    </p>
                  </div>
                  {!isDesktopDevice && (
                    <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full">Recommended</span>
                  )}
                </button>

                <button
                  onClick={() => setCaptureMode('file')}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-600 hover:border-purple-500 hover:bg-purple-900/10 transition-all group"
                >
                  <div className="w-14 h-14 rounded-full bg-purple-600/20 flex items-center justify-center group-hover:bg-purple-600/30 transition-colors">
                    <Upload className="w-7 h-7 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{t('upload_upload_file')}</p>
                    <p className="text-gray-500 text-xs mt-1">From your device or scanned copy</p>
                  </div>
                  {isDesktopDevice && (
                    <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full">Recommended</span>
                  )}
                </button>
              </div>
            )}

            {/* Desktop Document Handoff (when camera mode on desktop) */}
            {useHandoff && handoffType === 'document' && (
              <div className="bg-gray-800/60 border border-blue-500/30 rounded-xl p-8 mb-6 text-center">
                 <div className="bg-white p-4 rounded-xl inline-block mb-4">
                  <QRCodeSVG
                    value={handoffUrl || ''}
                    size={180}
                    level="H"
                  />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">Scan to Capture Document</h3>
                <p className="text-gray-400 max-w-sm mx-auto mb-6">
                  {handoffStatus === 'mobile_opened' 
                    ? 'Connected! Please take a photo of your ID on your mobile device.'
                    : 'Scan this QR code with your phone to use its camera for better quality.'}
                </p>

                <div className="flex items-center justify-center gap-3 mb-6">
                  {handoffStatus === 'waiting' && (
                    <div className="flex items-center text-blue-400 gap-2 animate-pulse">
                      <Smartphone className="w-5 h-5" />
                      <span>Waiting for connection...</span>
                    </div>
                  )}
                  {handoffStatus === 'mobile_opened' && (
                    <div className="flex items-center text-green-400 gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Device connected</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                     setUseHandoff(false);
                     setHandoffStatus('idle');
                     setHandoffUrl(null);
                     if (handoffPollRef.current) clearInterval(handoffPollRef.current);
                  }}
                  className="text-gray-500 hover:text-white transition-colors text-sm"
                >
                  Cancel and upload manually
                </button>
              </div>
            )}

            {/* Legacy Camera Mode Prompt (keeping as backup if needed, but primary is now handoff) */}
            {captureMode === 'camera' && isDesktopDevice && !documentPreview && !docCameraActive && !useHandoff && (
              <div className="bg-gray-800/60 border border-blue-500/30 rounded-xl p-6 mb-6 text-center">
                <Monitor className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-2">Use Your Phone Camera</h4>
                <p className="text-gray-400 text-sm mb-4">
                  For the best photo quality, take a picture of your ID using your phone. 
                  You can also upload a file if you prefer.
                </p>
                <div className="bg-gray-900/50 rounded-lg p-4 mb-4 inline-block">
                  <QRCodeSVG
                    value={window.location.href}
                    size={140}
                    bgColor="transparent"
                    fgColor="#60a5fa"
                    level="M"
                  />
                  <p className="text-gray-500 text-xs mt-2">Scan with your phone</p>
                </div>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setCaptureMode('file')}
                    className="px-4 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2 inline" /> Upload File Instead
                  </button>
                  <button
                    onClick={() => setCaptureMode('choose')}
                    className="px-4 py-2 text-gray-400 text-sm hover:text-white transition-colors"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {/* Mobile Camera Viewfinder */}
            {docCameraActive && (
              <div className="relative mb-6 rounded-xl overflow-hidden bg-black">
                <video
                  ref={docVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full max-h-[400px] object-cover"
                />
                {/* Document alignment overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-[85%] h-[55%] border-2 border-blue-400/60 rounded-lg relative">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-3 border-l-3 border-blue-400 rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-3 border-r-3 border-blue-400 rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-3 border-l-3 border-blue-400 rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-3 border-r-3 border-blue-400 rounded-br-lg" />
                    <p className="absolute -top-7 left-1/2 -translate-x-1/2 text-blue-300 text-xs font-medium whitespace-nowrap">
                      Align your ID within the frame
                    </p>
                  </div>
                </div>
                <canvas ref={docCanvasRef} className="hidden" />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                  <button
                    onClick={() => { stopDocCamera(); setCaptureMode('choose'); }}
                    className="px-4 py-2 bg-gray-800/80 text-white rounded-lg text-sm backdrop-blur"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={captureDocPhoto}
                    className="px-6 py-3 bg-blue-600 text-white rounded-full font-semibold shadow-lg hover:bg-blue-700 transition-colors"
                  >
                    <Camera className="w-5 h-5 inline mr-2" /> Capture
                  </button>
                </div>
              </div>
            )}

            {/* File Upload / Preview Area (shown when mode is 'file' or after capture) */}
            {(captureMode === 'file' || documentPreview) && !docCameraActive && (
              <div className={`grid gap-4 ${needsBack(selectedIdType as PhilippineIdType) ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-md mx-auto'}`}>
                {/* Front */}
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                  {documentPreview ? (
                    <div className="relative">
                      <img src={documentPreview} alt="ID front" className="max-h-48 mx-auto rounded-lg" />
                      <button
                        onClick={() => { setIdDocument(null); setDocumentPreview(null); setQualityResult(null); setQualityBlocked(false); }}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                        aria-label="Remove front image"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-300 text-sm mb-1 font-medium">{t('upload_front')}</p>
                      <p className="text-gray-500 text-xs mb-3">{t('upload_file_hint')}</p>
                      <input
                        type="file"
                        accept={ALLOWED_FILE_TYPES.join(',')}
                        onChange={e => e.target.files?.[0] && handleDocumentUpload(e.target.files[0], 'front')}
                        className="hidden"
                        id="id-front"
                      />
                      <label
                        htmlFor="id-front"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer"
                      >
                        <Upload className="w-4 h-4 mr-2" /> Select File
                      </label>
                    </>
                  )}
                  
                  {/* Quality Analysis Feedback — now BLOCKING */}
                  {documentPreview && qualityResult && (
                    <div className={`mt-3 p-3 rounded-lg border text-sm ${
                      qualityResult.isAcceptable 
                        ? 'bg-green-900/20 border-green-500/30 text-green-400' 
                        : 'bg-red-900/20 border-red-500/30 text-red-400'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {qualityResult.isAcceptable ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <span className="font-semibold">
                        {qualityResult.isAcceptable ? t('upload_quality_good') : t('upload_quality_issues')}
                        </span>
                      </div>
                      {!qualityResult.isAcceptable && qualityResult.issues.length > 0 && (
                        <>
                          <ul className="text-xs space-y-1 ml-6 mb-3">
                            {qualityResult.issues.map((issue, idx) => (
                              <li key={idx}>• {issue}</li>
                            ))}
                          </ul>
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={retakePhoto}
                              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
                            >
                              <RefreshCw className="w-3 h-3 mr-1 inline" /> {t('upload_retake')}
                            </button>
                            <button
                              onClick={overrideQualityBlock}
                              className="px-3 py-1.5 bg-gray-700 text-gray-300 text-xs rounded-lg hover:bg-gray-600 transition-colors"
                            >
                              {t('upload_use_anyway')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {isAnalyzingQuality && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-blue-400 text-xs">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Analyzing image quality...</span>
                    </div>
                  )}
                </div>

                {/* Back (if needed) */}
                {needsBack(selectedIdType as PhilippineIdType) && (
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                    {idDocumentBack ? (
                      <div className="relative">
                        <img src={URL.createObjectURL(idDocumentBack)} alt="ID back" className="max-h-48 mx-auto rounded-lg" />
                        <button
                          onClick={() => setIdDocumentBack(null)}
                          className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                          aria-label="Remove back image"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                        <p className="text-gray-300 text-sm mb-1 font-medium">Back of ID</p>
                        <p className="text-gray-500 text-xs mb-3">Optional but recommended</p>
                        {captureMode === 'camera' && !isDesktopDevice ? (
                          <button
                            onClick={() => startDocCamera('back')}
                            className="inline-flex items-center px-4 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600"
                          >
                            <Camera className="w-4 h-4 mr-2" /> Take Photo
                          </button>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept={ALLOWED_FILE_TYPES.join(',')}
                              onChange={e => e.target.files?.[0] && handleDocumentUpload(e.target.files[0], 'back')}
                              className="hidden"
                              id="id-back"
                            />
                            <label
                              htmlFor="id-back"
                              className="inline-flex items-center px-4 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600 cursor-pointer"
                            >
                              <Upload className="w-4 h-4 mr-2" /> Select File
                            </label>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Change mode link */}
            {captureMode !== 'choose' && !docCameraActive && !documentPreview && (
              <button
                onClick={() => setCaptureMode('choose')}
                className="mt-3 text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                ← Choose a different method
              </button>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={() => { goTo('select-id'); setCaptureMode('choose'); stopDocCamera(); }} className="flex items-center text-gray-400 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </button>
              <button
                onClick={runOCR}
                disabled={!idDocument || ocrLoading || qualityBlocked}
                className="flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {ocrLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('upload_extracting')}</>
                ) : (
                  <>{t('upload_extract')} <ChevronRight className="w-4 h-4 ml-1" /></>
                )}
              </button>
            </div>
          </div>
        );
      }

      // ── Step 4: Review OCR data ──
      case 'review-ocr':
        return (
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('ocr_title')}</h3>
            <p className="text-gray-400 mb-4">{t('ocr_sub')}</p>

            {/* Basic mode cleanup notice */}
            {verificationType === 'basic' && (
              <div className="mb-4 p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-300">
                  <span className="font-semibold">Name Cleanup Allowed: </span>
                  You may fix OCR errors (e.g. fix spacing, remove noise characters). 
                  For security, only words already found in the scanned text are accepted.
                </div>
              </div>
            )}

            {/* Authenticity badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm ${documentAuthentic
                ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                : 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                }`}>
                {documentAuthentic ? (
                  <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {t('ocr_authentic')}</>
                ) : (
                  <><AlertTriangle className="w-4 h-4 mr-1.5" /> {t('ocr_will_verify')}</>
                )}
              </div>

              {qrMatchResult && (
                <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm ${qrMatchResult.isMatch
                  ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                  : 'bg-red-900/30 text-red-400 border border-red-500/30 animate-pulse'
                  }`}>
                  {qrMatchResult.isMatch ? (
                    <><Shield className="w-4 h-4 mr-1.5" /> {t('ocr_qr_verified')}</>
                  ) : (
                    <><AlertTriangle className="w-4 h-4 mr-1.5" /> {t('ocr_qr_mismatch')}</>
                  )}
                </div>
              )}
            </div>

            {/* OCR Results */}
            <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4 mb-4">
              {Object.keys(ocrData).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(ocrData).map(([key, value]) => {
                    const isMismatched = verificationType === 'enhanced' &&
                      qrMatchResult &&
                      !qrMatchResult.isMatch &&
                      qrMatchResult.mismatchedFields.some(field =>
                        field.toLowerCase().includes(key.toLowerCase())
                      );

                    // Basic mode: check if all words in the edited value appear in raw OCR text
                    const isNameField = ['fullName', 'firstName', 'lastName', 'surname', 'givenNames', 'middleName'].includes(key);
                    const ocrUpper = rawOcrText.toUpperCase();
                    const isCleanupValid = !isNameField || verificationType !== 'basic' || rawOcrText === '' ||
                      value.trim().toUpperCase().split(/\s+/).every(token => token.length < 2 || ocrUpper.includes(token));

                    return (
                      <div key={key} className={`flex flex-col sm:flex-row sm:items-center gap-1 ${isMismatched ? 'bg-red-900/20 p-2 rounded border border-red-500/30' : ''}`}>
                        <span className="text-gray-400 text-sm capitalize min-w-[140px]">
                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                        </span>
                        <input
                          type="text"
                          value={value}
                          onChange={e => setOcrData(prev => ({ ...prev, [key]: e.target.value }))}
                          aria-label={key.replace(/([A-Z])/g, ' $1').trim()}
                          className={`flex-1 bg-gray-900/50 border rounded px-3 py-1.5 text-white text-sm focus:outline-none transition-colors ${
                            !isCleanupValid
                              ? 'border-red-500 focus:border-red-400'
                              : isMismatched
                              ? 'border-red-500 focus:border-red-400'
                              : 'border-gray-600 focus:border-blue-500'
                          }`}
                        />
                        {!isCleanupValid && (
                          <span className="text-red-400 text-xs font-medium">⚠ Word not in scan</span>
                        )}
                        {isMismatched && isCleanupValid && (
                          <span className="text-red-400 text-xs font-medium">⚠ Mismatch</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto mb-3 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-400" />
                  </div>
                  <p className="text-gray-300 font-medium mb-1">Preview extraction not available</p>
                  <p className="text-gray-500 text-sm max-w-md mx-auto">
                    Don't worry — our server will perform high-accuracy OCR after payment.
                    The preview uses browser-based OCR which may not work with all image types.
                  </p>
                </div>
              )}
            </div>

            {/* QR Data Comparison for Enhanced Tier */}
            {verificationType === 'enhanced' && qrData && Object.keys(qrData).length > 0 && (
              <div className="bg-blue-900/20 rounded-lg border border-blue-500/30 p-4 mb-4">
                <h4 className="text-sm font-semibold text-blue-400 mb-3 flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  PhilSys QR Data (Enhanced Verification)
                </h4>
                <div className="space-y-2 text-sm">
                  {Object.entries(qrData).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-gray-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className="text-blue-300 font-mono">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => goTo('upload-document')} className="flex items-center text-gray-400 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4 mr-1" /> Re-upload
              </button>
              <button
                onClick={handleSaveOCR}
                className="flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                disabled={ocrLoading}
              >
                {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4 ml-1" /></>}
              </button>
            </div>
          </div>
        );

      // ── Step 4.5: Biometric Consent ──
      case 'biometric-consent':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-flex p-3 rounded-full bg-blue-500/20 mb-4 text-blue-400">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{t('consent_title')}</h3>
              <p className="text-gray-400">{t('consent_sub')}</p>
            </div>

            <div className="glass-card p-6 border-blue-500/30 bg-blue-900/10 space-y-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                In compliance with the **Philippine Data Privacy Act (RA 10173)**, Agos requires your explicit consent to process your biometric data (selfie) for identity verification.
              </p>

              <ul className="space-y-3">
                <li className="flex items-start text-sm text-gray-400">
                  <Check className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('consent_purpose')}</span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Check className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('consent_retention')}</span>
                </li>
                <li className="flex items-start text-sm text-gray-400">
                  <Check className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('consent_security')}</span>
                </li>
              </ul>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <input
                id="biometric-consent"
                type="checkbox"
                checked={biometricConsent}
                onChange={(e) => setBiometricConsent(e.target.checked)}
                className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
              />
              <label htmlFor="biometric-consent" className="text-sm text-gray-300 cursor-pointer select-none">
                {t('consent_check')}
              </label>
            </div>

            <div className="flex justify-between pt-4">
              <button onClick={() => goTo('review-ocr')} className="flex items-center text-gray-400 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </button>
              <button
                disabled={!biometricConsent}
                onClick={() => {
                  if (biometricConsent) {
                    goTo('liveness-check');
                  }
                }}
                className={`flex items-center px-6 py-2 rounded-lg font-semibold transition-all ${biometricConsent
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/20'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {t('consent_proceed')} <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        );

      // ── Step 5: Liveness check ──
      case 'liveness-check':
        return (
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('liveness_title')}</h3>
            <p className="text-gray-400 mb-6">{t('liveness_sub')}</p>

            {/* Desktop Handoff Recommendation Banner */}
            {showDesktopHandoffPrompt && !desktopPromptDismissed && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6 p-4 bg-blue-900/30 border border-blue-500/40 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-white font-semibold text-sm mb-1">
                      📱 Better Results on Mobile
                    </h4>
                    <p className="text-gray-300 text-sm mb-3">
                      You're using a {getDeviceDescription()}. For the best camera quality and liveness detection, 
                      we recommend continuing on your mobile phone.
                    </p>
                    <button
                      onClick={() => setDesktopPromptDismissed(true)}
                      className="text-blue-400 hover:text-blue-300 text-xs font-medium"
                    >
                      Dismiss and use camera anyway
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Toggle: Camera vs Mobile Handoff */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => { setUseHandoff(false); setHandoffStatus('idle'); setHandoffUrl(null); if (handoffPollRef.current) clearInterval(handoffPollRef.current); startCamera(); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border transition-colors ${!useHandoff ? 'border-blue-500 bg-blue-900/20 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                  }`}
              >
                <Camera className="w-4 h-4" /> {t('liveness_use_camera')}
              </button>
              <button
                onClick={() => { setUseHandoff(true); setHandoffStatus('idle'); setHandoffUrl(null); if (handoffPollRef.current) clearInterval(handoffPollRef.current); stopCamera(); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border transition-colors ${useHandoff ? 'border-blue-500 bg-blue-900/20 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                  }`}
              >
                <Smartphone className="w-4 h-4" /> {t('liveness_mobile_handoff')}
              </button>
            </div>

            {!useHandoff ? (
              <div className="text-center">
                {selfieImage && (
                  <div className="relative max-w-sm mx-auto mb-4 bg-gray-800 rounded-lg overflow-hidden aspect-[4/3]">
                    <img src={selfieImage} alt="Selfie" className="w-full h-full object-cover" />
                  </div>
                )}

                {!selfieImage && !cameraActive && (
                  <div className="mb-6">
                    <div className="relative max-w-sm mx-auto mb-4 bg-gray-800 rounded-lg overflow-hidden aspect-[4/3] flex flex-col items-center justify-center text-gray-500">
                      <Camera className="w-12 h-12 mb-2" />
                      <p className="text-sm">{isAssisted ? 'Ready to Capture Selfie' : 'Ready for Liveness Check'}</p>
                    </div>
                    <button
                      onClick={startCamera}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
                    >
                      <Camera className="w-4 h-4 mr-2" /> {isAssisted ? 'Open Camera' : 'Start Liveness Wizard'}
                    </button>
                  </div>
                )}

                {cameraActive && !selfieImage && isAssisted && (
                  <div className="mb-6">
                    <div className="relative max-w-sm mx-auto mb-4 bg-black rounded-lg overflow-hidden aspect-[4/3] border-2 border-blue-500/30">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover transform scale-x-[-1]"
                      />
                      <div className="absolute inset-0 border-[40px] border-black/40 pointer-events-none">
                         <div className="w-full h-full border-2 border-dashed border-blue-400/50 rounded-[50%]" />
                      </div>
                    </div>
                    <button
                      onClick={captureSelfie}
                      className="px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 flex items-center mx-auto font-bold shadow-lg shadow-blue-500/20"
                    >
                      <Camera className="w-5 h-5 mr-2" /> Capture Photo
                    </button>
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                )}

                {selfieImage && (
                  <button
                    onClick={() => { setSelfieImage(null); setLivenessResult(null); startCamera(); }}
                    className="px-5 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 flex items-center mx-auto mb-4"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Retake
                  </button>
                )}

                {/* Results Summary */}
                {livenessResult && (
                  <div className={`mt-2 inline-flex items-center px-3 py-1.5 rounded-full text-sm ${livenessResult.passed
                    ? 'bg-green-900/30 text-green-400'
                    : 'bg-red-900/30 text-red-400'
                    }`}>
                    {livenessResult.passed ? <CheckCircle2 className="w-4 h-4 mr-1.5" /> : <XCircle className="w-4 h-4 mr-1.5" />}
                    {livenessResult.passed ? t('liveness_passed') : t('liveness_failed')}
                  </div>
                )}
              </div>
            ) : null}

            {!useHandoff && cameraActive && !selfieImage && !isAssisted && (
              <div className="mt-4">
                <LivenessCheck
                  onComplete={async (result) => {
                    setLivenessResult({ passed: result.passed, score: result.score });
                    if (result.passed && result.selfieBlob) {
                      setSelfieBlob(result.selfieBlob);
                      const selfieUrl = URL.createObjectURL(result.selfieBlob);
                      setSelfieImage(selfieUrl);

                      // Face comparison with ID document
                      if (idDocument) {
                        try {
                          const selfieImg = new Image();
                          selfieImg.src = selfieUrl;
                          await new Promise(resolve => { selfieImg.onload = resolve; });

                          const idImg = new Image();
                          idImg.src = URL.createObjectURL(idDocument);
                          await new Promise(resolve => { idImg.onload = resolve; });

                          const match = await compareFaces(idImg, selfieImg);

                          if (match) {
                            setCameraActive(false);
                            // Pass the blob directly to avoid state race condition
                            handleBundlePayment(result.selfieBlob); 
                          } else {
                            // MATCH FAILED: Do NOT reset. Let user decide.
                            setError('Face match verification was low. You may retake or proceed for manual review.');
                            setCameraActive(false);
                            // We keep selfieImage and livenessResult so the user sees the "Complete" button
                          }
                        } catch (err) {
                           // ERROR (e.g. "No face detected"): Stop loop, show error.
                           const msg = err instanceof Error ? err.message : 'Comparison failed';
                           setError(msg);
                           setCameraActive(false);
                        }
                      } else {
                        setCameraActive(false);
                        handleBundlePayment(); // Transition to payment/processing
                      }
                    } else if (!result.passed) {
                      setError('Liveness check failed. Please try again with better lighting.');
                    }
                  }}
                  onCancel={() => {
                    setCameraActive(false);
                    setError('Verification cancelled.');
                  }}
                  language="en"
                />
              </div>
            )}

            {useHandoff && (
              /* Mobile handoff UI */
              <div className="text-center bg-gray-800/50 rounded-lg border border-gray-700/50 p-8">
                <Smartphone className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h4 className="text-white font-medium mb-2">Continue on your phone</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Scan the QR code or open the link on your phone to complete the liveness check
                </p>

                {/* QR Code Section */}
                {handoffStatus === 'idle' && (
                  <div className="space-y-4">
                    <p className="text-gray-400 text-sm">
                      Click the button below to generate a QR code for mobile handoff
                    </p>
                    <button
                      type="button"
                      onClick={() => createHandoffSession('liveness')}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Generate QR Code
                    </button>
                  </div>
                )}

                {handoffStatus === 'creating' && (
                  <div className="space-y-4">
                    <Loader2 className="w-8 h-8 text-blue-400 mx-auto animate-spin" />
                    <p className="text-gray-400 text-sm">Creating handoff session...</p>
                  </div>
                )}

                {handoffStatus === 'waiting' && handoffUrl && (
                  <div className="space-y-4">
                    {/* Actual QR Code */}
                    <div className="w-48 h-48 mx-auto p-3 bg-white rounded-lg">
                      <QRCodeSVG
                        value={handoffUrl}
                        size={168}
                        level="M"
                        includeMargin={false}
                      />
                    </div>

                    {/* Copy URL button */}
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={copyHandoffUrl}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy Link
                      </button>
                      <a
                        href={handoffUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open Link
                      </a>
                    </div>

                    <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Waiting for mobile device...</span>
                    </div>
                  </div>
                )}

                {handoffStatus === 'completed' && (
                  <div className="space-y-4">
                    <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
                    <p className="text-green-400 font-medium">Liveness check completed on mobile!</p>
                  </div>
                )}

                {handoffStatus === 'error' && (
                  <div className="space-y-4">
                    <XCircle className="w-12 h-12 text-red-400 mx-auto" />
                    <p className="text-red-400 text-sm">Handoff failed. Please try again.</p>
                    <button
                      type="button"
                      onClick={() => { setHandoffStatus('idle'); setHandoffUrl(null); }}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      {t('result_try_again')}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={() => { stopCamera(); goTo('review-ocr'); }} className="flex items-center text-gray-400 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </button>
              {livenessResult?.passed && (
                <button
                  onClick={() => handleBundlePayment()}
                  disabled={paymentProcessing}
                  className="flex items-center px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {paymentProcessing ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {t('liveness_processing')}</>
                  ) : (
                    <>{t('liveness_complete')} <ChevronRight className="w-4 h-4 ml-1" /></>
                  )}
                </button>
              )}
            </div>
          </div>
        );

      // ── Step 7: Processing ──
      case 'processing':
        return (
          <div className="text-center py-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="inline-block mb-6"
            >
              <div className="relative">
                <img
                  src="/pwa-192x192.png"
                  alt="Agos Logo"
                  className="w-16 h-16 object-contain"
                />
              </div>
            </motion.div>

            <h3 className="text-xl font-semibold text-white mb-2">{t('processing_title')}</h3>
            <p className="text-gray-400 mb-8">{t('processing_sub')}</p>

            <div className="max-w-sm mx-auto space-y-3">
              {[
                { label: t('processing_uploading'), done: processingStage !== 'uploading' },
                { label: t('processing_ocr'), done: processingStage === 'blockchain' || processingStage === 'done' },
                { label: t('processing_scoring'), done: processingStage === 'blockchain' || processingStage === 'done' },
                { label: t('processing_record'), done: processingStage === 'done' },
              ].map((item, i) => (
                <div key={i} className="flex items-center text-sm">
                  {item.done ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0 animate-spin" />
                  )}
                  <span className={item.done ? 'text-green-400' : 'text-blue-400'}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      // ── Step 8: Result ──
      case 'result':
        return (
          <div className="text-center py-6">
            {verificationStatus === 'verified' ? (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 10 }}
                  className="inline-block mb-4"
                >
                  <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-12 h-12 text-green-400" />
                  </div>
                </motion.div>

                <h3 className="text-2xl font-bold text-white mb-2">{t('result_title')}</h3>
                <p className="text-gray-400 mb-6">Your identity has been verified and a tamper-proof digital record has been issued.</p>

                <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4 max-w-md mx-auto text-left space-y-3">
                  {verificationId && (
                    <div>
                      <span className="text-gray-400 text-xs block">Verification ID</span>
                      <span className="text-white text-sm font-mono">{verificationId}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-400 text-xs block">Digital Signature</span>
                    <span className="text-green-400 text-sm font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      RSA-4096 signed by Agos
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs block">Status</span>
                    <span className="text-green-400 text-sm font-medium">{t('result_verified')}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs block">Type</span>
                    <span className="text-white text-sm capitalize">{verificationType} verification</span>
                  </div>
                  {serverResult && (
                    <>
                      <div>
                        <span className="text-gray-400 text-xs block">Confidence Score</span>
                        <span className="text-white text-sm font-medium">
                          {Math.round(serverResult.confidenceScore * 100)}%
                        </span>
                      </div>
                      {serverResult.faceResult && (
                        <div>
                          <span className="text-gray-400 text-xs block">Face Match</span>
                          <span className={`text-sm font-medium ${serverResult.faceResult.matched ? 'text-green-400' : 'text-red-400'}`}>
                            {serverResult.faceResult.matched ? 'Matched' : 'Not Matched'}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={async () => {
                      if (!verificationId) return;

                      try {
                        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/verifications/${verificationId}/certificate`, {
                          headers: {
                            'Accept': 'application/pdf',
                          },
                          credentials: 'include',
                        });

                        if (!response.ok) {
                          throw new Error('Failed to download certificate');
                        }

                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Agos-Digital-ID-${verificationId.substring(0, 8)}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (error) {
                        console.error('Download failed:', error);
                        alert('Failed to download certificate. Please try again.');
                      }
                    }}
                    className="flex items-center justify-center px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg shadow-blue-500/20"
                  >
                    <Download className="w-5 h-5 mr-2" /> {t('result_download')}
                  </button>
                  <button
                    onClick={() => {
                      setCurrentStep('choose-type');
                      setVerificationType('basic');
                      setSelectedIdType('');
                      setIdDocument(null);
                      setIdDocumentBack(null);
                      setDocumentPreview(null);
                      setOcrData({});
                      setSelfieImage(null);
                      setLivenessResult(null);
                      setVerificationId(null);
                      setBlockchainTxHash(null);
                      setVerificationStatus('pending');
                      setSelfieBlob(null);
                      setServerResult(null);
                      setProcessingStage('uploading');
                    }}
                    className="flex items-center justify-center px-5 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                  >
                    <RefreshCw className="w-5 h-5 mr-2" /> {t('result_new')}
                  </button>
                </div>

                <p className="mt-4 text-xs text-gray-500 max-w-sm mx-auto">{t('result_signed_note')}</p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-12 h-12 text-red-400" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-2">{t('result_failed_title')}</h3>
                <p className="text-gray-400 mb-6">{t('result_failed_sub')}</p>

                {serverResult?.failureReasons && serverResult.failureReasons.length > 0 && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6 max-w-md mx-auto text-left">
                    <p className="text-red-400 text-sm font-semibold mb-2">{t('result_reasons')}</p>
                    <ul className="list-disc list-inside text-red-300 text-sm space-y-1">
                      {serverResult.failureReasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {/* Free retry — reuses existing record and credits */}
                  {verificationId && (
                    <button
                      onClick={async () => {
                        try {
                          setError(null);
                          setVerificationStatus('pending');
                          goTo('processing');
                          setProcessingStage('processing');
                          const res = await fetch(`/api/verifications/${verificationId}/retry`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'X-CSRF-Token': getCsrf() },
                          });
                          const data = await res.json();
                          if (data.success) {
                            setVerificationStatus(data.data.status === 'verified' ? 'verified' : 'failed');
                            setServerResult(prev => ({
                              ...prev!,
                              failureReasons: data.data.failureReasons,
                              confidenceScore: data.data.confidenceScore,
                            }));
                          } else {
                            setError(data.error || 'Retry failed. Please start a new verification.');
                            setVerificationStatus('failed');
                          }
                          goTo('result');
                        } catch (err) {
                          setError('Retry request failed. Please try again.');
                          setVerificationStatus('failed');
                          goTo('result');
                        }
                      }}
                      className="flex items-center justify-center px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> {t('result_try_again')} (libre)
                    </button>
                  )}
                  {/* New verification — uses a new credit */}
                  <button
                    onClick={() => goTo('choose-type')}
                    className="flex items-center justify-center px-5 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
                  >
                    Bagong Verification
                  </button>
                </div>
              </>
            )
            }
          </div >
        );
    }
  };

  // ── Main Render ──────────────────────────────
  return (
    <>
      {/* Demo Consent Modal */}
      {showDemoConsent && !demoConsentGiven && (
        <DemoConsentModal
          onConsent={() => {
            setDemoConsentGiven(true);
            setShowDemoConsent(false);
            logDemoEvent('consent_accepted', { timestamp: new Date().toISOString() });
          }}
          onDecline={() => {
            setShowDemoConsent(false);
            logDemoEvent('consent_declined', { timestamp: new Date().toISOString() });
          }}
        />
      )}

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-1">Identity Authenticity Assessment</h2>
          <p className="text-sm font-medium text-blue-400/80 mb-6">
            Engineered for BSP eKYC & NPC Compliance
          </p>
        </div>

      <ProgressBar />

      {/* Error banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-500/30 flex items-start"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 ml-2" aria-label="Dismiss error">
              <XCircle className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step content */}
      <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
      </div>
    </>
  );
}
